import Barba from '@barba/core'
import prefetch from '@barba/prefetch'
import noop from '/utils/noop'

// Init the Barba wrapper
export function bootstrap ({
  views = [],
  components = { global: [], local: [] },
  afterEnter = noop
} = {}) {
  const refs = new Set()

  // Barba's own history handling strips the url hash while it navigates
  // (it replaceState()s it away right after pushing it), so it's gone by
  // the time `afterEnter` fires. Capture it here, before Barba gets to
  // the click, and restore it once Barba is done with the transition.
  let pendingHash = ''
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href*="#"]')
    if (!link) return
    try {
      pendingHash = new URL(link.href, window.location.href).hash
    } catch (err) {}
  }, true)

  function restorePendingHash (next) {
    if (!pendingHash || window.location.hash === pendingHash) return
    window.history.replaceState(null, '', pendingHash)
    if (next?.url) next.url.hash = pendingHash
    pendingHash = ''
  }

  Barba.use(prefetch, { timeout: 2000 })
  Barba.init({
    debug: import.meta.env.DEV,
    prevent: ({ el }) => el?.classList?.contains('no-barba'),
    views: views.map(view => view.default),
    transitions: [{
      sync: false,
      once: data => {
        try {
          // Find and hydrate all global components
          for (const { selector, hydrate } of components.global) {
            Array.from(document.querySelectorAll(selector)).map(hydrate)
          }

          // The "once" transition runs instead of (not before) enter/afterEnter
          // on the very first page load, so local components must be hydrated
          // here too, or they never run at all for a page opened directly.
          for (const { selector, hydrate } of components.local) {
            for (const el of document.querySelectorAll(selector)) {
              refs.add(hydrate(el))
            }
          }

          restorePendingHash(data.next)
          afterEnter(data.next)

          // Flag document as ready
          document.body.classList.add('is-ready')
        } catch (e) {
          console.error(e)
        }
      }
    }]
  })

  Barba.hooks.afterEnter(({ next }) => {
    try {
      // Hydrate all possible components, keeping a ref
      for (const { selector, hydrate } of components.local) {
        for (const el of next.container.querySelectorAll(selector)) {
          refs.add(hydrate(el))
        }
      }

      restorePendingHash(next)
      afterEnter(next)

      // Flag document as ready
      document.body.classList.add('is-ready')
    } catch (e) {
      console.error(e)
    }
  })

  Barba.hooks.afterLeave(() => {
    try {
      // Destroy previous components refs if any
      for (const ref of refs) ref?.destroy?.()
      refs.clear()
    } catch (e) {
      console.error(e)
    }
  })
}

// Describe a BarbaView used in Barba.bootstrap
export class BarbaView {
  constructor (namespace, hooks) {
    this.namespace = namespace
    this.hooks = hooks
  }

  _callHook (hookName, data) {
    if (!this.hooks[hookName]) return
    this.hooks[hookName].call(this, data)
  }

  // SEE https://barba.js.org/docs/advanced/hooks/#Base-hooks
  beforeEnter (data) {
    this.refs = {}
    this.state = {}

    this._callHook('beforeEnter', data)
  }

  afterEnter (data) {
    this._callHook('afterEnter', data)
  }

  beforeLeave (data) {
    this._callHook('beforeLeave', data)
  }

  afterLeave (data) {
    this._callHook('afterLeave', data)

    for (const k in this.refs) {
      this.refs[k]?.destroy?.()
      delete this.refs[k]
    }

    for (const k in this.state) delete this.state[k]

    this.refs = {}
    this.state = {}
  }
}

export default { bootstrap, BarbaView }
